import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

const gold = '#B8943A';
const ink = '#2E2A26';
const sub = '#6B6560';
const linen = '#F2EFE8';

async function fetchInvoices() {
  const res = await fetch('/api/student/invoices', { credentials: 'include' });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.data;
}

export default function StudentInvoicesPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['student-invoices'],
    queryFn: fetchInvoices,
  });

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-IQ', { style: 'currency', currency }).format(amount);

  const formatDate = (ts: any) => {
    if (!ts) return '\u2014';
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div style={{ fontFamily: S.font, maxWidth: 800 }}>
      <h1 style={{ fontFamily: S.heading, fontSize: 24, fontWeight: 700, color: ink, marginBottom: 8 }}>{isRTL ? 'فواتيري' : 'My Invoices'}</h1>
      <p style={{ color: sub, fontSize: 13, marginBottom: 24 }}>{isRTL ? 'تحميل فواتير تسجيلاتك في الدورات.' : 'Download invoices for your course enrollments.'}</p>

      {isLoading ? (
        <p style={{ color: sub, padding: 40, textAlign: 'center' }}>Loading...</p>
      ) : invoices.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid ' + linen, padding: 40, textAlign: 'center' }}>
          <p style={{ color: sub }}>No invoices yet. Invoices are generated when you enroll in a course.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {invoices.map((inv: any) => (
            <div key={inv.id} style={{
              background: 'white', borderRadius: 12, border: '1px solid ' + linen,
              padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontWeight: 600, color: ink, fontSize: 14 }}>{inv.invoiceNumber}</div>
                <div style={{ color: sub, fontSize: 12, marginTop: 2 }}>{inv.courseTitle}</div>
                <div style={{ color: sub, fontSize: 11, marginTop: 2 }}>{formatDate(inv.issuedAt)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ fontWeight: 600, color: ink, fontSize: 14 }}>{formatCurrency(inv.subtotal, inv.currency)}</span>
                <button
                  onClick={() => window.open('/api/student/invoices/' + inv.id + '/pdf', '_blank')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                    background: 'linear-gradient(135deg, ' + gold + ', #CCA94E)',
                    color: ink, border: 'none', borderRadius: 8, cursor: 'pointer',
                    fontFamily: S.font, fontSize: 12, fontWeight: 600,
                  }}
                >
                  <Download size={14} /> PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
